package com.evoapps.evoshift.ui.kiosk

import com.evoapps.evoshift.widgets.rememberStableText
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.VolunteerActivism
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.evoapps.evoshift.domain.model.KioskClockResult
import com.evoapps.evoshift.domain.model.KioskPerson
import com.evoapps.evoshift.domain.model.KioskTipPool
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.tips.money
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.ui.util.dayAndDate
import com.evoapps.evoshift.viewmodels.KioskTipsStep
import com.evoapps.evoshift.viewmodels.KioskTipsUiState
import com.evoapps.evoshift.viewmodels.KioskTipsViewModel
import com.evoapps.evoshift.viewmodels.KioskViewModel
import com.evoapps.evoshift.widgets.GoldButton
import com.evoapps.evoshift.widgets.UserAvatar
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.close
import evoshift.composeapp.generated.resources.kiosk_enter_pin
import evoshift.composeapp.generated.resources.kiosk_tip_share
import evoshift.composeapp.generated.resources.kiosk_tips
import evoshift.composeapp.generated.resources.kiosk_tips_amount_title
import evoshift.composeapp.generated.resources.kiosk_tips_change
import evoshift.composeapp.generated.resources.kiosk_tips_continue
import evoshift.composeapp.generated.resources.kiosk_tips_none_yet
import evoshift.composeapp.generated.resources.kiosk_tips_split
import evoshift.composeapp.generated.resources.kiosk_tips_text
import evoshift.composeapp.generated.resources.kiosk_tips_total
import evoshift.composeapp.generated.resources.kiosk_tips_who
import evoshift.composeapp.generated.resources.tips_amount
import evoshift.composeapp.generated.resources.tips_hours
import evoshift.composeapp.generated.resources.tips_tax_note
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.stringResource

/** Test tags of the tablet's tips. */
object KioskTipsTags {
    const val OPEN = "kiosk_tips"
    const val AMOUNT = "kiosk_tips_amount"
    const val SHARE = "kiosk_tip_share"
}

/** SH-124: "Tips" in the tablet's header (on the grid, when a manager is on the team). */
@Composable
fun KioskTipsButton(onClick: () -> Unit) {
    val label = stringResource(Res.string.kiosk_tips)
    IconButton(onClick = onClick, modifier = Modifier.size(56.dp).testTag(KioskTipsTags.OPEN).semantics { contentDescription = label }) {
        Icon(Icons.Default.VolunteerActivism, contentDescription = null, tint = Color.White.copy(alpha = 0.6f))
    }
}

/**
 * SH-124: the day's tips on the tablet, instead of the grid while open: a manager picks
 * themselves, their PIN, the amount, then the split with the note that tax is the employer's and
 * accountant's.
 */
@Composable
fun KioskTipsFlow(state: KioskTipsUiState, managers: List<KioskPerson>, viewModel: KioskTipsViewModel) {
    when (val step = state.step) {
        KioskTipsStep.Closed -> Unit
        KioskTipsStep.PickManager -> Column(Modifier.fillMaxSize().padding(horizontal = 16.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                BackButton(viewModel::close)
                Column {
                    Text(stringResource(Res.string.kiosk_tips_who), color = Color.White, fontSize = 24.sp, fontWeight = FontWeight.Bold)
                    Text(stringResource(Res.string.kiosk_tips_text), color = Color.White.copy(alpha = 0.7f))
                }
            }
            state.error?.let { Error(it.asString()) }
            Spacer(Modifier.height(16.dp))
            LazyVerticalGrid(
                columns = GridCells.Adaptive(minSize = 180.dp),
                horizontalArrangement = Arrangement.spacedBy(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp),
                modifier = Modifier.weight(1f)
            ) {
                items(managers, key = { it.userId }) { person -> PersonTile(person, subtitle = null) { viewModel.pickManager(person) } }
            }
            TextButton(onClick = viewModel::close) { Text(stringResource(Res.string.close), color = LightGold) }
        }
        is KioskTipsStep.Pin -> Centered(onBack = viewModel::close) {
            Who(step.manager)
            Text(stringResource(Res.string.kiosk_enter_pin), color = Color.White.copy(alpha = 0.8f), style = MaterialTheme.typography.titleMedium)
            PinDots(step.digits.length)
            state.error?.let { Error(it.asString()) }
            PinPad(viewModel::digit, viewModel::backspace)
            GoldButton(
                text = stringResource(Res.string.kiosk_tips_continue),
                onClick = viewModel::submitPin,
                enabled = step.digits.length == KioskViewModel.PIN_LENGTH,
                isBusy = state.isSending,
                modifier = Modifier.widthIn(max = 360.dp).fillMaxWidth().heightIn(min = 72.dp)
            )
        }
        is KioskTipsStep.Amount -> Centered(onBack = viewModel::close) {
            Who(step.manager)
            Text(stringResource(Res.string.kiosk_tips_amount_title, dateLabel(step.pool.date)), color = Color.White, fontSize = 24.sp, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center)
            rememberStableText(step.text, viewModel::setAmount).let { stable ->
                OutlinedTextField(
                    value = stable.value,
                    onValueChange = stable.onValueChange,
                    label = { Text(stringResource(Res.string.tips_amount)) },
                    singleLine = true,
                    textStyle = TextStyle(fontSize = 32.sp, color = Color.White, textAlign = TextAlign.Center),
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = LightGold, unfocusedBorderColor = Color.White.copy(alpha = 0.5f),
                        focusedLabelColor = LightGold, unfocusedLabelColor = Color.White.copy(alpha = 0.7f), cursorColor = LightGold
                    ),
                    modifier = Modifier.widthIn(max = 360.dp).fillMaxWidth().testTag(KioskTipsTags.AMOUNT)
                )
            }
            state.error?.let { Error(it.asString()) }
            GoldButton(
                text = stringResource(Res.string.kiosk_tips_split),
                onClick = viewModel::submitAmount,
                enabled = step.text.isNotBlank(),
                isBusy = state.isSending,
                modifier = Modifier.widthIn(max = 360.dp).fillMaxWidth().heightIn(min = 72.dp)
            )
            Note()
        }
        is KioskTipsStep.Done -> Centered(onBack = viewModel::close) {
            Text(stringResource(Res.string.kiosk_tips_total, money(step.pool.amountMinor, step.pool.currency)), color = Color.White, fontSize = 28.sp, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center)
            Text(dateLabel(step.pool.date), color = LightGold, style = MaterialTheme.typography.titleMedium)
            Split(step.pool)
            Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                TextButton(onClick = viewModel::changeAmount) { Text(stringResource(Res.string.kiosk_tips_change), color = LightGold) }
                TextButton(onClick = viewModel::close) { Text(stringResource(Res.string.close), color = LightGold) }
            }
            Note()
        }
    }
}

/**
 * SH-124: on the clock-out confirmation, the person's own share of the day's tips, when a manager
 * entered the day's pool ("Your tips today: 85,00 zł"). Nothing otherwise.
 */
@Composable
fun KioskTipShareLine(result: KioskClockResult) {
    val share = result.tipShareMinor ?: return
    Text(
        stringResource(Res.string.kiosk_tip_share, money(share, result.tipCurrency ?: "PLN")),
        color = LightGold,
        fontSize = 24.sp,
        fontWeight = FontWeight.Bold,
        textAlign = TextAlign.Center,
        modifier = Modifier.padding(top = 16.dp).testTag(KioskTipsTags.SHARE)
    )
}

@Composable
private fun Centered(onBack: () -> Unit, content: @Composable () -> Unit) {
    Column(Modifier.fillMaxSize().padding(16.dp)) {
        BackButton(onBack)
        Column(
            Modifier.fillMaxSize().verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) { content() }
    }
}

@Composable
private fun Who(person: KioskPerson) {
    UserAvatar(name = person.displayName, photoUrl = person.photoUrl, size = 80.dp)
    Text(person.displayName, color = Color.White, fontSize = 24.sp, fontWeight = FontWeight.Bold)
}

@Composable
private fun Error(text: String) {
    Text(text, color = Color(0xFFFF8A80), textAlign = TextAlign.Center, style = MaterialTheme.typography.titleMedium)
}

@Composable
private fun Note() {
    Text(
        stringResource(Res.string.tips_tax_note),
        color = Color.White.copy(alpha = 0.6f),
        style = MaterialTheme.typography.bodySmall,
        textAlign = TextAlign.Center,
        modifier = Modifier.widthIn(max = 480.dp)
    )
}

@Composable
private fun Split(pool: KioskTipPool) {
    if (pool.shares.isEmpty()) {
        Text(stringResource(Res.string.kiosk_tips_none_yet), color = Color.White.copy(alpha = 0.8f), textAlign = TextAlign.Center)
        return
    }
    Column(Modifier.widthIn(max = 480.dp).fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
        pool.shares.forEach { share ->
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(share.displayName, color = Color.White, fontSize = 20.sp, modifier = Modifier.weight(1f))
                Text(stringResource(Res.string.tips_hours, com.evoapps.evoshift.ui.tips.hoursText(share.minutesWorked)), color = Color.White.copy(alpha = 0.6f))
                Spacer(Modifier.size(16.dp))
                Text(money(share.amountMinor, pool.currency), color = LightGold, fontSize = 20.sp, fontWeight = FontWeight.Bold)
            }
        }
    }
}

@Composable
private fun dateLabel(date: String?): String =
    date?.let { runCatching { LocalDate.parse(it) }.getOrNull() }?.let { dayAndDate(it) } ?: ""
